<template>
  <div class="admin_wrapper">

    <!-- ログイン画面 -->
    <div v-if="!isLoggedIn" class="login_box">
      <img src="@/assets/mik_logo.svg" alt="MIK" class="admin_logo">
      <h2>管理者ログイン</h2>
      <input
        :type="showPassword ? 'text' : 'password'"
        v-model="inputPassword"
        placeholder="パスワードを入力"
        class="password_input"
        @keyup.enter="login"
      >
      <label class="show_pw_label">
        <input type="checkbox" v-model="showPassword"> パスワードを表示
      </label>
      <p v-if="loginError" class="error_msg">パスワードが違います。</p>
      <button class="admin_btn" @click="login">ログイン</button>
    </div>

    <!-- 管理画面 -->
    <div v-else class="admin_panel">
      <div class="admin_header">
        <div class="admin_header_left">
          <img src="@/assets/mik_logo.svg" alt="MIK" class="admin_logo_small">
          <h2>応募管理画面</h2>
        </div>
        <div class="admin_header_right">
          <span class="count_badge">全 {{ filteredApplications.length }} 件</span>
          <button class="logout_btn" @click="logout">ログアウト</button>
        </div>
      </div>

      <!-- フィルター -->
      <div class="filter_bar">
        <select v-model="filterStatus" class="filter_select">
          <option value="">すべてのステータス</option>
          <option value="未対応">未対応</option>
          <option value="対応中">対応中</option>
          <option value="対応済">対応済</option>
        </select>
        <select v-model="filterArea" class="filter_select">
          <option value="">すべての勤務地</option>
          <option v-for="area in areas" :key="area" :value="area">{{ area }}</option>
        </select>
        <input
          type="text"
          v-model="searchKeyword"
          placeholder="氏名・メールで検索"
          class="search_input"
        >
        <button class="reload_btn" @click="fetchApplications">🔄 再読み込み</button>
      </div>

      <!-- 一括操作バー -->
      <div v-if="selectedCount > 0" class="bulk_bar">
        <span class="bulk_count">{{ selectedCount }} 件を選択中</span>
        <button class="bulk_clear_btn" @click="clearSelection" :disabled="isDeleting">選択を解除</button>
        <button class="bulk_delete_btn" @click="deleteSelected" :disabled="isDeleting">
          {{ isDeleting ? `削除中... (${deleteProgress}/${selectedCount})` : '選択した応募を削除' }}
        </button>
      </div>

      <!-- ローディング -->
      <div v-if="isLoading" class="loading_msg">読み込み中...</div>
      <div v-else-if="filteredApplications.length === 0" class="empty_msg">該当する応募情報がありません。</div>

      <!-- 応募一覧テーブル -->
      <div v-else class="table_wrapper">
        <table class="app_table">
          <thead>
            <tr>
              <th class="th_check">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  :indeterminate.prop="selectedCount > 0 && !isAllSelected"
                  @change="toggleSelectAll"
                  title="表示中の応募をすべて選択"
                >
              </th>
              <th>受付日時</th>
              <th>氏名</th>
              <th>氏名(カナ)</th>
              <th>生まれ年</th>
              <th>電話番号</th>
              <th>メール</th>
              <th>希望職種</th>
              <th>希望勤務地</th>
              <th>面談希望日</th>
              <th>要望</th>
              <th>ステータス</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="app in filteredApplications"
              :key="app.id"
              :id="`row-${app.id}`"
              :class="['row_' + statusClass(app.status), { row_target: app.id === targetId, row_selected: selectedIds.has(app.id) }]"
            >
              <td class="td_check">
                <input type="checkbox" :checked="selectedIds.has(app.id)" @change="toggleSelect(app.id)">
              </td>
              <td class="td_date">{{ formatDate(app.created_at) }}</td>
              <td>{{ app.name }}</td>
              <td>{{ app.kana }}</td>
              <td>{{ app.birth_year }}年</td>
              <td>{{ app.phone || '—' }}</td>
              <td>
                <a :href="'mailto:' + app.mail" class="mail_link">{{ app.mail }}</a>
              </td>
              <td>{{ app.job_type || '—' }}</td>
              <td>{{ app.job_area || '—' }}</td>
              <td class="td_mendan">
                <div>①{{ app.mendan1 || '—' }}</div>
                <div>②{{ app.mendan2 || '—' }}</div>
                <div>③{{ app.mendan3 || '—' }}</div>
              </td>
              <td class="td_youbou">{{ app.youbou || '—' }}</td>
              <td>
                <select
                  :value="app.status"
                  @change="updateStatus(app.id, $event.target.value)"
                  class="status_select"
                  :class="'status_' + statusClass(app.status)"
                >
                  <option value="未対応">未対応</option>
                  <option value="対応中">対応中</option>
                  <option value="対応済">対応済</option>
                </select>
              </td>
              <td>
                <button class="delete_btn" @click="deleteApplication(app.id, app.name)">削除</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, reactive, computed, watch, nextTick } from 'vue';

export default {
  name: 'AdminView',
  setup() {
  

    const inputPassword = ref('');
    const loginError = ref(false);
    const isLoggedIn = ref(false);
    const storedPassword = ref('');
    const showPassword = ref(false);

    // URLの ?id= パラメータを取得
    // /#admin?id=xxx の形でidを取得（ハッシュ内のクエリパラメータを解析）
    const hashQuery = window.location.hash.includes('?')
      ? window.location.hash.split('?')[1]
      : window.location.search.slice(1);
    const targetId = new URLSearchParams(hashQuery).get('id') || '';

    const applications = ref([]);
    const isLoading = ref(false);
    const filterStatus = ref('');
    const filterArea = ref('');
    const searchKeyword = ref('');
    const areas = ref(['札幌', '盛岡', '東京', '大阪', '福岡']);

    // セッション中パスワードを保持
    const login = async () => {
      if (!inputPassword.value) return;
      const res = await fetch('/api/applications', { headers: { 'X-Admin-Password': inputPassword.value } });
      if (res.ok) {
        isLoggedIn.value = true;
        storedPassword.value = inputPassword.value;
        loginError.value = false;
        applications.value = await res.json();
        // URLに ?id= がある場合、ログイン後に対象行へスクロール
        if (targetId) {
          await nextTick();
          const el = document.getElementById(`row-${targetId}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        loginError.value = true;
      }
    };

    const logout = () => {
      isLoggedIn.value = false;
      storedPassword.value = '';
      inputPassword.value = '';
      applications.value = [];
    };

    const fetchApplications = async () => {
      isLoading.value = true;
      try {
        const res = await fetch('/api/applications', { headers: { 'X-Admin-Password': storedPassword.value } });
        if (res.ok) {
          applications.value = await res.json();
          // 新しい順に並び替え
          applications.value.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }
      } finally {
        isLoading.value = false;
      }
    };

    const updateStatus = async (id, status) => {
      await fetch('/api/applications', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': storedPassword.value },
        body: JSON.stringify({ id, status }),
      });
      const app = applications.value.find(a => a.id === id);
      if (app) app.status = status;
    };

    const deleteApplication = async (id, name) => {
      if (!confirm(`「${name}」さんの応募情報を削除しますか？`)) return;
      const res = await fetch('/api/applications', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': storedPassword.value },
        body: JSON.stringify({ id }),
      });
      if (res.ok) {
        applications.value = applications.value.filter(a => a.id !== id);
        selectedIds.delete(id);
      }
    };

    // ===== 一括選択・一括削除 =====
    const selectedIds = reactive(new Set());
    const isDeleting = ref(false);
    const deleteProgress = ref(0);

    const selectedCount = computed(() => selectedIds.size);
    const isAllSelected = computed(() =>
      filteredApplications.value.length > 0 &&
      filteredApplications.value.every(a => selectedIds.has(a.id))
    );

    const toggleSelect = (id) => {
      if (selectedIds.has(id)) selectedIds.delete(id);
      else selectedIds.add(id);
    };

    // 表示中（フィルター後）の応募をすべて選択／解除
    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        filteredApplications.value.forEach(a => selectedIds.delete(a.id));
      } else {
        filteredApplications.value.forEach(a => selectedIds.add(a.id));
      }
    };

    const clearSelection = () => selectedIds.clear();

    const deleteSelected = async () => {
      const ids = [...selectedIds];
      if (ids.length === 0 || isDeleting.value) return;
      if (!confirm(`選択した ${ids.length} 件の応募情報を削除します。\nこの操作は取り消せません。よろしいですか？`)) return;

      isDeleting.value = true;
      deleteProgress.value = 0;
      const deleted = [];
      try {
        // 500件ずつ送信
        for (let i = 0; i < ids.length; i += 500) {
          const chunk = ids.slice(i, i + 500);
          const res = await fetch('/api/applications', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', 'X-Admin-Password': storedPassword.value },
            body: JSON.stringify({ ids: chunk }),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || `HTTP ${res.status}`);
          }
          deleted.push(...chunk);
          deleteProgress.value = deleted.length;
        }
      } catch (err) {
        alert(`削除中にエラーが発生しました（${deleted.length} 件は削除済み）：${err.message}`);
      } finally {
        const deletedSet = new Set(deleted);
        applications.value = applications.value.filter(a => !deletedSet.has(a.id));
        deleted.forEach(id => selectedIds.delete(id));
        isDeleting.value = false;
      }
    };

    const filteredApplications = computed(() => {
      return applications.value.filter(app => {
        const matchStatus = !filterStatus.value || app.status === filterStatus.value;
        const matchArea = !filterArea.value || app.job_area === filterArea.value;
        const keyword = searchKeyword.value.toLowerCase();
        const matchKeyword = !keyword ||
          (app.name && app.name.toLowerCase().includes(keyword)) ||
          (app.kana && app.kana.toLowerCase().includes(keyword)) ||
          (app.mail && app.mail.toLowerCase().includes(keyword));
        return matchStatus && matchArea && matchKeyword;
      });
    });

    // フィルター条件が変わったら選択をリセット（見えていない行を誤って削除しないため）
    watch([filterStatus, filterArea, searchKeyword], () => selectedIds.clear());

    const formatDate = (iso) => {
      if (!iso) return '—';
      const d = new Date(iso);
      return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    };

    const statusClass = (status) => {
      if (status === '未対応') return 'new';
      if (status === '対応中') return 'inprogress';
      if (status === '対応済') return 'done';
      return '';
    };

    return {
      inputPassword, loginError, isLoggedIn, showPassword,
      login, logout, targetId,
      applications, filteredApplications,
      isLoading, filterStatus, filterArea, searchKeyword, areas,
      fetchApplications, updateStatus, deleteApplication,
      selectedIds, selectedCount, isAllSelected, isDeleting, deleteProgress,
      toggleSelect, toggleSelectAll, clearSelection, deleteSelected,
      formatDate, statusClass,
    };
  }
};
</script>

<style scoped>
.admin_wrapper {
  min-height: 100vh;
  background-color: #f4f6f9;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ログイン */
.login_box {
  margin-top: 120px;
  background: white;
  padding: 50px 60px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.admin_logo {
  width: 180px;
  margin-bottom: 10px;
}

.login_box h2 {
  margin: 0;
  font-size: 22px;
  color: #2c3e50;
}

.password_input {
  width: 260px;
  height: 42px;
  padding: 0 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 16px;
}

.show_pw_label {
  font-size: 13px;
  color: #555;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.error_msg {
  color: #e74c3c;
  font-size: 13px;
  margin: 0;
}

.admin_btn {
  width: 200px;
  height: 44px;
  background-color: #0cc0df;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: 200ms;
}
.admin_btn:hover { background-color: #0aa8c4; }

/* 管理パネル */
.admin_panel {
  width: 100%;
  max-width: 1400px;
  padding: 20px;
  box-sizing: border-box;
}

.admin_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 16px 24px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 16px;
}
.admin_header_left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.admin_logo_small { width: 100px; }
.admin_header h2 { margin: 0; font-size: 20px; color: #2c3e50; }
.admin_header_right { display: flex; align-items: center; gap: 12px; }

.count_badge {
  background-color: #0cc0df;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
}

.logout_btn {
  padding: 8px 18px;
  background-color: #ecf0f1;
  border: 1px solid #bdc3c7;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}
.logout_btn:hover { background-color: #d5dbdb; }

/* フィルター */
.filter_bar {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  background: white;
  padding: 14px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 16px;
  align-items: center;
}
.filter_select, .search_input {
  height: 36px;
  padding: 0 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
}
.search_input { width: 200px; }
.reload_btn {
  padding: 6px 14px;
  background-color: #ecf0f1;
  border: 1px solid #bdc3c7;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}
.reload_btn:hover { background-color: #d5dbdb; }

/* テーブル */
.table_wrapper {
  overflow-x: auto;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.app_table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.app_table th {
  background-color: #2c3e50;
  color: white;
  padding: 12px 10px;
  text-align: left;
  white-space: nowrap;
}
.app_table td {
  padding: 10px;
  border-bottom: 1px solid #ecf0f1;
  vertical-align: top;
}
.row_new { background-color: #fef9f0; }
.row_inprogress { background-color: #f0f7ff; }
.row_done { background-color: #f5fff5; }
.row_target { outline: 3px solid #0cc0df; background-color: #e8f9fc !important; animation: highlight_fade 2s ease 0.5s forwards; }
@keyframes highlight_fade { to { outline-color: transparent; } }

.td_date { white-space: nowrap; font-size: 13px; color: #666; }
.td_mendan { font-size: 13px; white-space: nowrap; }
.td_youbou { max-width: 160px; font-size: 13px; word-break: break-all; }

.mail_link { color: #0cc0df; text-decoration: none; }
.mail_link:hover { text-decoration: underline; }

.status_select {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 13px;
  cursor: pointer;
}
.status_new { background-color: #ffeaa7; }
.status_inprogress { background-color: #74b9ff; color: white; }
.status_done { background-color: #55efc4; }

.delete_btn {
  padding: 4px 10px;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}
.delete_btn:hover { background-color: #c0392b; }

/* 一括操作 */
.th_check, .td_check { width: 36px; text-align: center; }
.th_check input, .td_check input { width: 16px; height: 16px; cursor: pointer; }
.row_selected td { background-color: #fdecea; }
.bulk_bar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff5f4;
  border: 1px solid #f5b7b1;
  padding: 10px 20px;
  border-radius: 10px;
  margin-bottom: 16px;
}
.bulk_count { font-weight: bold; color: #c0392b; margin-right: auto; }
.bulk_clear_btn {
  padding: 6px 14px;
  background-color: #ecf0f1;
  border: 1px solid #bdc3c7;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}
.bulk_delete_btn {
  padding: 6px 16px;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
}
.bulk_delete_btn:hover:not(:disabled) { background-color: #c0392b; }
.bulk_delete_btn:disabled, .bulk_clear_btn:disabled { opacity: 0.6; cursor: not-allowed; }

.loading_msg, .empty_msg {
  text-align: center;
  padding: 40px;
  color: #666;
  background: white;
  border-radius: 10px;
}
</style>
